import React, { useEffect, useState } from 'react';
import { Button } from '../ui/button';
import AdminFileBox from './AdminFileBox';
import { BUSINESS_TYPES as FALLBACK_BT, TG_CITIES, adminAuthHeaders } from './adminHelpers';
import { BadgeCheck, ShieldOff } from 'lucide-react';
import BusinessHoursEditor from '../BusinessHoursEditor';
import { DEFAULT_HOURS } from '../../lib/businessHours';

const AdminVendorForm = ({ initial, onSave, onCancel }) => {
  const isUpdate = !!initial?.id;
  const [plans, setPlans] = useState([]);
  const [sellerTypes, setSellerTypes] = useState([]);
  const [businessTypes, setBusinessTypes] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [claimed, setClaimed] = useState(!!initial?.claimed);
  const [claimedAt, setClaimedAt] = useState(initial?.claimed_at || null);
  const [claimToggling, setClaimToggling] = useState(false);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/plans`).then((r) => r.json()).then(setPlans).catch(() => {});
    // Admin form pulls the FULL Seller Type catalogue (including admin-only
    // restricted badges) — that's why we pass include_verified_only=true.
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/seller-types?include_verified_only=true`)
      .then((r) => r.json()).then(setSellerTypes).catch(() => setSellerTypes([]));
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/business-types`)
      .then((r) => r.json()).then(setBusinessTypes).catch(() => setBusinessTypes([]));
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/districts`)
      .then((r) => r.json()).then(setDistricts).catch(() => setDistricts([]));
  }, []);

  const toggleClaim = async () => {
    if (!isUpdate) return;
    const next = !claimed;
    const verb = next ? 'mark this business as CLAIMED & verified' : 'mark this business as UNCLAIMED';
    if (!window.confirm(`Are you sure you want to ${verb}?`)) return;
    setClaimToggling(true);
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/vendors/${initial.id}/claim-status`, {
        method: 'PUT',
        headers: adminAuthHeaders({ 'Content-Type': 'application/json' }),
        credentials: 'include',
        body: JSON.stringify({ claimed: next }),
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body.detail || `HTTP ${r.status}`);
      }
      const doc = await r.json();
      setClaimed(!!doc.claimed);
      setClaimedAt(doc.claimed_at || null);
    } catch (err) {
      alert(`Failed to update claim status: ${err.message}`);
    } finally {
      setClaimToggling(false);
    }
  };

  const [v, setV] = useState(() => ({
    name: initial?.name || '',
    contact_name: initial?.contact_name || '',
    mobile: initial?.mobile || '',
    email: initial?.email || '',
    website: initial?.website || '',
    gst_number: initial?.gst_number || '',
    city: initial?.city ? (initial.city.split(',')[0].trim()) : '',
    district: initial?.district || '',
    year_established: initial?.established || '',
    business_type: initial?.business_type || '',
    seller_type: initial?.seller_type || '',
    categories: Array.isArray(initial?.categories) ? initial.categories.join(', ') : (initial?.categories || ''),
    description: initial?.description || '',
    status: initial?.status || 'active',
    plan_id: initial?.plan_id || '',
    _companyDoc: null,
    _gstDoc: null,
    _logoImage: null,
    business_hours: (initial?.business_hours && Object.keys(initial.business_hours).length) ? initial.business_hours : DEFAULT_HOURS,
  }));
  const set = (k, val) => setV((prev) => ({ ...prev, [k]: val }));

  const onFile = (k) => (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    set(k, file);
  };

  return (
    <form onSubmit={(e) => { e.preventDefault(); onSave(v); }} className="space-y-4">
      {isUpdate && initial?.vendor_code && (
        <div className="text-xs font-mono uppercase tracking-wider bg-gray-100 border border-gray-200 rounded px-2 py-1 inline-block" data-testid="admin-vendor-code">
          Vendor ID: <span className="font-bold text-[#ee5e23]">{initial.vendor_code}</span>
        </div>
      )}
      {isUpdate && (
        <div
          className={`flex items-center justify-between gap-3 p-3 rounded-md border ${claimed ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}
          data-testid="admin-claim-panel"
        >
          <div className="flex items-center gap-2">
            {claimed ? (
              <BadgeCheck className="w-5 h-5 text-emerald-600" />
            ) : (
              <ShieldOff className="w-5 h-5 text-amber-600" />
            )}
            <div>
              <div className={`text-sm font-semibold ${claimed ? 'text-emerald-800' : 'text-amber-800'}`}>
                {claimed ? 'Claimed & Verified Business' : 'Unclaimed Business'}
              </div>
              <div className="text-[11px] text-gray-600">
                {claimed
                  ? `Verified by owner${claimedAt ? ` on ${new Date(claimedAt).toLocaleDateString()}` : ''}.`
                  : 'Owner has not yet verified ownership via OTP.'}
              </div>
            </div>
          </div>
          <Button
            type="button"
            onClick={toggleClaim}
            disabled={claimToggling}
            variant="outline"
            className={`text-xs ${claimed ? 'border-red-300 text-red-700 hover:bg-red-50' : 'border-emerald-300 text-emerald-700 hover:bg-emerald-50'}`}
            data-testid="admin-toggle-claim-btn"
          >
            {claimToggling ? 'Updating…' : claimed ? 'Mark as Unclaimed' : 'Mark as Claimed'}
          </Button>
        </div>
      )}
      {isUpdate && (initial?.logo_url || initial?.logo) && (
        <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-md">
          {initial.logo_url ? (
            <img src={initial.logo_url} alt="" className="w-14 h-14 rounded-md object-cover bg-white border border-gray-200" />
          ) : (
            <div className="w-14 h-14 rounded-md bg-gradient-to-br from-[#2c54b2] to-[#1e3a8a] text-white flex items-center justify-center font-bold">{initial.logo}</div>
          )}
          <div className="text-xs text-gray-600">Current logo. Upload a new one below to replace.</div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2">
          <label className="text-xs font-medium">Company Name *</label>
          <input className="w-full mt-1 px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-[#ee5e23]" value={v.name} onChange={(e) => set('name', e.target.value)} required />
        </div>
        <div>
          <label className="text-xs font-medium">Contact Person</label>
          <input className="w-full mt-1 px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-[#ee5e23]" value={v.contact_name} onChange={(e) => set('contact_name', e.target.value)} />
        </div>
        <div>
          <label className="text-xs font-medium">Mobile</label>
          <input className="w-full mt-1 px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-[#ee5e23]" value={v.mobile} onChange={(e) => set('mobile', e.target.value.replace(/\D/g, '').slice(0, 10))} placeholder="10-digit" />
        </div>
        <div>
          <label className="text-xs font-medium">Email</label>
          <input className="w-full mt-1 px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-[#ee5e23]" type="email" value={v.email} onChange={(e) => set('email', e.target.value)} />
        </div>
        <div>
          <label className="text-xs font-medium">Website</label>
          <input
            className="w-full mt-1 px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-[#ee5e23]"
            type="url"
            inputMode="url"
            value={v.website}
            onChange={(e) => set('website', e.target.value)}
            placeholder="https://yourcompany.com"
            data-testid="admin-vendor-website-input"
          />
        </div>
        <div>
          <label className="text-xs font-medium">GST Number</label>
          <input className="w-full mt-1 px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-[#ee5e23]" value={v.gst_number} onChange={(e) => set('gst_number', e.target.value.toUpperCase())} maxLength={15} />
        </div>
        <div>
          <label className="text-xs font-medium">District</label>
          <select className="w-full mt-1 px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-[#ee5e23] bg-white" value={v.district} onChange={(e) => set('district', e.target.value)} data-testid="admin-vendor-district-select">
            <option value="">Select district</option>
            {districts.map((d) => <option key={d.id} value={d.name}>{d.name}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs font-medium">City *</label>
          <select className="w-full mt-1 px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-[#ee5e23] bg-white" value={v.city} onChange={(e) => set('city', e.target.value)} required>
            <option value="">Select city</option>
            {TG_CITIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs font-medium">Year Established</label>
          <input type="number" className="w-full mt-1 px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-[#ee5e23]" value={v.year_established} onChange={(e) => set('year_established', e.target.value)} />
        </div>
        <div>
          <label className="text-xs font-medium">Business Type</label>
          <select className="w-full mt-1 px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-[#ee5e23] bg-white" value={v.business_type} onChange={(e) => set('business_type', e.target.value)} data-testid="admin-vendor-business-type-select">
            <option value="">Select</option>
            {(businessTypes.length ? businessTypes.map((b) => b.name) : FALLBACK_BT).map((b) => <option key={b} value={b}>{b}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs font-medium">Seller Type</label>
          <select
            className="w-full mt-1 px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-[#ee5e23] bg-white"
            value={v.seller_type}
            onChange={(e) => set('seller_type', e.target.value)}
            data-testid="admin-vendor-seller-type-select"
          >
            <option value="">— None —</option>
            {sellerTypes.map((st) => (
              <option key={st.id} value={st.name}>{st.name}{st.verified_only ? ' (admin-only)' : ''}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-xs font-medium">Status *</label>
          <select className="w-full mt-1 px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-[#ee5e23] bg-white" value={v.status} onChange={(e) => set('status', e.target.value)}>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
        <div className="col-span-2">
          <label className="text-xs font-medium">Subscription Plan</label>
          <select className="w-full mt-1 px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-[#ee5e23] bg-white" value={v.plan_id} onChange={(e) => set('plan_id', e.target.value)}>
            <option value="">— No Plan —</option>
            {plans.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.price === 0 ? 'Free' : `₹${p.price}`})</option>)}
          </select>
        </div>
        <div className="col-span-2">
          <label className="text-xs font-medium">Categories (comma separated)</label>
          <input className="w-full mt-1 px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-[#ee5e23]" value={v.categories} onChange={(e) => set('categories', e.target.value)} placeholder="Steel Pipes, TMT Bars" />
        </div>
        <div className="col-span-2">
          <label className="text-xs font-medium">Description</label>
          <textarea className="w-full mt-1 px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-[#ee5e23]" value={v.description} onChange={(e) => set('description', e.target.value)} rows={3} />
        </div>
        <div className="col-span-2">
          <label className="text-xs font-medium block mb-1">Business Hours</label>
          <BusinessHoursEditor
            value={v.business_hours}
            onChange={(next) => set('business_hours', next)}
            testidPrefix="admin-vendor-hours"
          />
        </div>
      </div>

      <div className="border-t border-gray-200 pt-4">
        <h3 className="text-sm font-bold text-gray-900 mb-1">Logo & Documents</h3>
        <p className="text-xs text-gray-500 mb-3">PDF / JPG / PNG / WEBP, max 5 MB. Leave blank to keep existing.</p>
        <div className="grid grid-cols-3 gap-3">
          <AdminFileBox label="Logo (image)" file={v._logoImage} existingUrl={initial?.logo_url} onChange={onFile('_logoImage')} onClear={() => set('_logoImage', null)} accept=".jpg,.jpeg,.png,.webp,image/*" />
          <AdminFileBox label="Company Doc" file={v._companyDoc} existingUrl={initial?.company_doc_url} onChange={onFile('_companyDoc')} onClear={() => set('_companyDoc', null)} />
          <AdminFileBox label="GST Doc" file={v._gstDoc} existingUrl={initial?.gst_doc_url} onChange={onFile('_gstDoc')} onClear={() => set('_gstDoc', null)} />
        </div>
      </div>

      <div className="flex gap-2 justify-end pt-2 border-t border-gray-200">
        <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
        <Button type="submit" className="bg-[#ee5e23] hover:bg-[#d24e15] text-white">{isUpdate ? 'Update Vendor' : 'Create Vendor'}</Button>
      </div>
    </form>
  );
};

export default AdminVendorForm;
